import React, { useEffect, useState } from 'react';
import ContainerFlex from '~/components/containerFlex';
import TableList from '~/components/tableList';
import endpoints from '~/services/endpoints';
import { FormField, setFormState } from '~/utils/form';
import * as St from './styles';
import useApi, { ApiMethod } from '~/hooks/useApi';
import { icons } from '~/assets';
import Dialog from '~/components/dialog';
import Container from '~/components/container';
import moment from 'moment';
import { IListColumn } from '~/models/listCol';
import Loading from '~/components/loading';
import Pagination from '~/components/pagination';

interface Form {
  dataVencimento: FormField;
  mesReferencia: FormField;
  ano: FormField;
}

interface IResponseListFilesCharge {
  data: {
    arquivo: string;
    dataFechamento: string;
    dataGeracao: string;
    dataVencimento: string;
    urlDownload: string;
  }[];
  totalPages: number;
  totalSize: number;
  page: number;
  pageSize: number;

}

interface IDownloadFileCharge {
  base64: string;
  filename: string;
}

const FileCharge: React.FC = () => {
  const [form, setForm] = useState<Form>({
    dataVencimento: { name: 'dataVencimento', value: '' },
    mesReferencia: { name: 'mesReferencia', value: '' },
    ano: { name: 'ano', value: '' },
  });
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState<string | null>();
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const header = [
    { id: 1, value: 'Nome do arquivo', maxChar: 20, textAlign: 'center' },
    { id: 2, value: 'Data de fechamento', maxChar: 20, textAlign: 'center' },
    { id: 3, value: 'Data de vencimento', maxChar: 20, textAlign: 'center' },
    { id: 4, value: 'Data e hora da geração', maxChar: 20, textAlign: 'center' },
    { id: 5, value: 'Ação de download', maxChar: 10, textAlign: 'center' },
  ];
  // Request para API chamando o endpoint da criação do relatório
  const RequestFileChargeDownload = useApi<IDownloadFileCharge>(
    endpoints.report.getDownloadFileCharge,
    ApiMethod.GET,
    {},
    true,
  );

  const RequestListFileCharge = useApi<IResponseListFilesCharge>(
    endpoints.report.getListFilesCharge,
    ApiMethod.GET,
    {},
    true,
  );

  // Método para consultar o retorno do relatório e fazer
  // o download automático do arquivo em XLS
  const onClickDownloadExcel = async (filename: string) => {
    setLoading(true);
    try {
      const responseReport = await RequestFileChargeDownload.callApi({
        arquivo: filename,
      });

      if (responseReport.status >= 200 && responseReport.status <= 300) {
        const binary = atob(responseReport.data.base64);
        const buffer = new ArrayBuffer(binary.length);
        const view = new Uint8Array(buffer);
        const contentType = 'application/vnd.ms-excel';

        for (let i = 0; i < binary.length; i += 1) {
          view[i] = binary.charCodeAt(i);
        }
        const url = window.URL.createObjectURL(
          new Blob([view], { type: contentType }),
        );

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', responseReport.data.filename);
        link.click();
        window.URL.revokeObjectURL(url);
        setLoading(false);
      } else {
        setLoading(false);
        setDialogIsOpen(true);
        setError(responseReport.mensagem || 'Não há arquivos para esta data.');
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const getListFilesCharge = async (page: number = 1) => {
    setLoading(true);
    try {
      const responseListFilesCharge = await RequestListFileCharge.callApi({
        page,
        pageSize: 10,
      });

      if (responseListFilesCharge.status === 200) {
        const responseData = responseListFilesCharge.data;

        setTotalPages(responseData?.totalPages || 0);
        setTotalItems(responseData?.totalSize || 0);

        const newList = [];

        responseData?.data?.forEach((item) => {
          const row: IListColumn[] = [
            {
              id: 1,
              title: 'Nome do arquivo',
              value: item.arquivo,
              maxChar: 20,
              textAlign: 'center',
            },
            {
              id: 2,
              title: 'Data de fechamento',
              value: item?.dataFechamento ? moment(item.dataFechamento).format('DD/MM/YYYY') : '-',
              maxChar: 20,
              textAlign: 'center',
            },
            {
              id: 3,
              title: 'Data de vencimento',
              value: item?.dataVencimento ? moment(item.dataVencimento).format('DD/MM/YYYY') : '-',
              maxChar: 20,
              textAlign: 'center',
            },
            {
              id: 4,
              title: 'Data e hora da geração',
              value: item?.dataGeracao ? moment(item.dataGeracao).format('DD/MM/YYYY HH:mm') : '-',
              maxChar: 20,
              textAlign: 'center',
            },
            {
              id: 5,
              item: item,
              action4: () => onClickDownloadExcel(item.arquivo),
              maxChar: 10,
              textAlign: 'center',
            },
          ];
          newList.push(row);
        });
        setList(newList);
        setLoading(false);

        if (responseData?.data?.length === 0) {
          setDialogIsOpen(true);
          setError('Dados de cobrança não encontrados');
        }
      } else {
        setLoading(false);
        setDialogIsOpen(true);
        setError(responseListFilesCharge.mensagem || 'Erro ao consultar os arquivos de cobrança.');
      }

    } catch (error) {
      console.log(error);
      setLoading(false);
      setDialogIsOpen(true);
      setError(error.mensagem || 'Erro ao consultar os arquivos de cobrança.');
    }
  }

  useEffect(() => {
    getListFilesCharge();
  }, [])

  return (
    <>
      <Loading status={loading} />
      <Dialog
        icon={icons.exclamation}
        open={dialogIsOpen}
        positiveLabel="Entendi"
        positiveAction={() => setDialogIsOpen(false)}
        message={error}
      />
      <Container>
        {list?.length > 0 ? (
          <St.BoxCard>
            <ContainerFlex>
              <ContainerFlex padding={16} style={{ width: '100%' }}>
                <TableList customerHeader={header} list={list} />
              </ContainerFlex>
              <ContainerFlex row horizontal marginTop={20} justifyContent='space-between' padding={20}>
                <ContainerFlex>
                  <span style={{ fontWeight: 'bold' }}>
                    {`Resultado: ${totalItems}`}
                  </span>
                </ContainerFlex>

                <Pagination
                  totalPages={totalPages}
                  onCurrentPageChange={getListFilesCharge}
                />

              </ContainerFlex>
            </ContainerFlex>
          </St.BoxCard>
        ) : !error ? (
          <St.BoxCard>
            <ContainerFlex center>
              <span>Dados de cobrança não encontrados</span>
            </ContainerFlex>
          </St.BoxCard>
        ) : null}
        {error && (
          <St.BoxCard>
            <ContainerFlex center marginTop={30}>
              <St.TitleCategory>{error}</St.TitleCategory>
            </ContainerFlex>
          </St.BoxCard>
        )}
      </Container>
    </>
  );
};

export default FileCharge;
